import { Component } from '@theme/component';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { onAnimationEnd } from '@theme/utilities';

/**
 * @typedef {Object} ProductVariant
 * @property {string|number} [id] - Variant ID
 * @property {string} [title] - Variant title
 * @property {string} [name] - Variant name
 * @property {boolean} [available] - Whether variant is available
 * @property {Object} [featured_media] - Featured media object
 * @property {Object} [featured_media.preview_image] - Preview image data
 * @property {string} [featured_media.preview_image.src] - Image source URL
 * @property {string} [featured_media.alt] - Alt text for the image
 */

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar - The floating bar container
 * @property {HTMLButtonElement} addToCartButton - Sticky bar's button
 * @property {HTMLImageElement} productImage - Product image element
 */

/**
 * A custom element that manages a sticky add-to-cart bar.
 * Shows when the main buy buttons scroll out of view.
 *
 * @extends {Component<StickyAddToCartRefs>}
 */
class StickyAddToCartComponent extends Component {
  requiredRefs = ['stickyBar', 'addToCartButton'];

  /** @type {IntersectionObserver | null} */
  #buyButtonsIntersectionObserver = null;

  /** @type {IntersectionObserver | null} */
  #mainBottomObserver = null;

  /** @type {number | undefined} */
  #resetTimeout;

  /** @type {boolean} */
  #isStuck = false;

  /** @type {number | null} */
  #animationTimeout = null;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {HTMLButtonElement | null} */
  #targetAddToCartButton = null;

  /** @type {number} */
  #currentQuantity = 1;

  /** @type {boolean} */
  #hiddenByBottom = false;

  /** @type {ProductVariant[] | null} */
  #variants = null;

  connectedCallback() {
    super.connectedCallback();

    this.#setupIntersectionObserver();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected, { signal });

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.cartError, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });

    this.#getInitialQuantity();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#buyButtonsIntersectionObserver?.disconnect();
    this.#mainBottomObserver?.disconnect();
    this.#abortController.abort();
    if (this.#animationTimeout) {
      clearTimeout(this.#animationTimeout);
    }
  }

  /**
   * Sets up the IntersectionObserver to watch the buy buttons visibility
   */
  #setupIntersectionObserver() {
    const productForm = this.#getProductForm();
    if (!productForm) return;

    const buyButtonsBlock = productForm.closest('.buy-buttons-block');
    if (!buyButtonsBlock) return;

    // In themes migrated from 2.0, the footer element doesn't exist
    const footer = document.querySelector('footer') ?? document.querySelector('[class*="footer-group"]');
    if (!footer) return;

    // Observer for buy buttons visibility
    this.#buyButtonsIntersectionObserver = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (!entry) return;

      // Only show sticky bar if buy buttons have been scrolled past (above viewport)
      if (!entry.isIntersecting && !this.#isStuck) {
        // Check if the element is above the viewport (scrolled past) or below (not yet reached)
        const rect = entry.target.getBoundingClientRect();
        if (rect.bottom < 0 || rect.top < 0) {
          // Element is above viewport - show sticky bar
          this.#showStickyBar();
        }
        // If rect.top >= 0, element is below viewport - don't show sticky bar yet
      } else if (entry.isIntersecting && this.#isStuck) {
        this.#hiddenByBottom = false;
        this.#hideStickyBar();
      }
    });

    // Observer for footer visibility - hides sticky bar at page bottom
    this.#mainBottomObserver = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry) return;

        if (entry.isIntersecting && this.#isStuck) {
          this.#hiddenByBottom = true;
          this.#hideStickyBar();
        } else if (!entry.isIntersecting && this.#hiddenByBottom) {
          // Footer out of view - check if we should show sticky bar again
          const rect = buyButtonsBlock.getBoundingClientRect();
          // Only show if buy buttons are above the viewport (scrolled past)
          if (rect.bottom < 0 || rect.top < 0) {
            this.#hiddenByBottom = false;
            this.#showStickyBar();
          }
        }
      },
      {
        rootMargin: '200px 0px 0px 0px',
      }
    );

    this.#buyButtonsIntersectionObserver.observe(buyButtonsBlock);
    this.#mainBottomObserver.observe(footer);
    this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
  }

  // Public action handlers
  /**
   * Handles the add to cart button click in the sticky bar
   */
  handleAddToCartClick = async () => {
    if (!this.#targetAddToCartButton) return;
    this.#syncTargetQuantity();
    this.#targetAddToCartButton.dataset.puppet = 'true';
    this.#targetAddToCartButton.click();
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    if (this.refs.addToCartButton.dataset.added !== 'true') {
      this.refs.addToCartButton.dataset.added = 'true';
    }

    if (!cartIcon || !this.refs.addToCartButton || !this.refs.productImage) return;
    if (this.#resetTimeout) clearTimeout(this.#resetTimeout);

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));
    flyToCartElement.classList.add('fly-to-cart--sticky');
    flyToCartElement.style.setProperty('background-image', `url(${this.refs.productImage.src})`);
    flyToCartElement.useSourceSize = 'true';
    flyToCartElement.source = this.refs.productImage;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);

    await onAnimationEnd([this.refs.addToCartButton, flyToCartElement]);
    this.#resetTimeout = setTimeout(() => {
      this.refs.addToCartButton.removeAttribute('data-added');
    }, 800);
  };

  /**
   * Handles variant update events
   * @param {CustomEvent} event - The variant update event
   */
  #handleVariantUpdate = (event) => {
    if (event.detail.data.productId !== this.dataset.productId) return;

    const variant = event.detail.resource;

    // Get the new sticky add to cart HTML from the server response
    const newStickyAddToCart = event.detail.data.html.querySelector('sticky-add-to-cart');
    if (!newStickyAddToCart) return;

    const newStickyBar = newStickyAddToCart.querySelector('[ref="stickyBar"]');
    if (!newStickyBar) return;

    // Store current visibility state before morphing
    const currentStuck = this.refs.stickyBar.getAttribute('data-stuck') || 'false';
    const variantAvailable = newStickyAddToCart.dataset.variantAvailable;

    // Morph the entire sticky bar content
    morph(this.refs.stickyBar, newStickyBar, { childrenOnly: true });

    // Restore visibility state after morphing
    this.refs.stickyBar.setAttribute('data-stuck', currentStuck);
    this.dataset.variantAvailable = variantAvailable;
    this.#variants = null;

    // Update the dataset attributes with new variant info
    if (variant && variant.id) {
      this.dataset.currentVariantId = variant.id;
    }

    // Re-cache the target add to cart button after morphing
    const productForm = this.#getProductForm();
    if (productForm) {
      this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
    }

    if (variant == null) {
      this.#handleVariantUnavailable();
    }

    this.#syncStickyQuantityInput();
  };

  /**
   * Handles variant selected events
   * @param {CustomEvent} event - The variant selected event
   */
  #handleVariantSelected = (event) => {
    // The variant update event will follow and handle all updates via morph
    // We just update the dataset here for tracking
    const variantId = event.detail.resource?.id;
    if (!variantId) return;
    this.dataset.currentVariantId = variantId;
    this.#syncStickyVariantPills(variantId);
  };

  /**
   * Handles variant selection from the sticky bar pills
   * @param {Event} event
   */
  handleVariantPillClick = (event) => {
    const target = event.target;
    const button = target instanceof HTMLElement ? target.closest('.sticky-add-to-cart__variant-pill') : null;
    if (!(button instanceof HTMLButtonElement)) return;

    const selectedVariantId = button.dataset.variantId;
    if (!selectedVariantId) return;

    this.dataset.currentVariantId = selectedVariantId;
    this.#syncStickyVariantPills(selectedVariantId);

    const selectedVariant = this.#getVariants().find((variant) => String(variant.id) === selectedVariantId);
    const mainVariantPicker = this.#getMainVariantPicker();

    if (!selectedVariant || !mainVariantPicker) return;

    const radioFieldsets = Array.from(mainVariantPicker.querySelectorAll('fieldset[data-fieldset-index]'));
    const dropdowns = Array.from(mainVariantPicker.querySelectorAll('.variant-option--dropdowns select'));
    const optionValues = selectedVariant.options || [];

    /** @type {HTMLElement | null} */
    let lastChangedControl = null;

    if (radioFieldsets.length > 0) {
      optionValues.forEach((optionValue, index) => {
        const fieldset = radioFieldsets[index];
        if (!(fieldset instanceof HTMLElement)) return;

        const matchingInput = Array.from(fieldset.querySelectorAll('input')).find(
          (input) => input instanceof HTMLInputElement && input.value === optionValue
        );

        if (matchingInput instanceof HTMLInputElement) {
          matchingInput.checked = true;
          lastChangedControl = matchingInput;
        }
      });
    } else if (dropdowns.length > 0) {
      optionValues.forEach((optionValue, index) => {
        const select = dropdowns[index];
        if (!(select instanceof HTMLSelectElement)) return;

        select.value = optionValue;
        Array.from(select.options).forEach((option) => {
          option.selected = option.value === optionValue;
          if (option.selected) {
            option.setAttribute('selected', 'selected');
          } else {
            option.removeAttribute('selected');
          }
        });
        lastChangedControl = select;
      });
    }

    lastChangedControl?.dispatchEvent(new Event('change', { bubbles: true }));
  };

  /**
   * Updates the variant title based on selected options when the variant is unavailable
   */
  #handleVariantUnavailable = () => {
    this.dataset.currentVariantId = '';
  };

  /**
   * Handles cart add complete (success or error) - resets puppet flag
   * @param {CustomEvent} _event - The cart event (unused)
   */
  #handleCartAddComplete = (_event) => {
    // Reset the puppet flag after cart operation
    if (this.#targetAddToCartButton) {
      this.#targetAddToCartButton.dataset.puppet = 'false';
    }
  };

  /**
   * Handles quantity selector update events
   * @param {QuantitySelectorUpdateEvent} event - The quantity update event
   */
  #handleQuantityUpdate = (event) => {
    // Only respond to product page quantity selector updates, not cart drawer
    if (event.detail.cartLine) return;

    const stickyQuantityInput = this.#getStickyQuantityInput();
    const eventTarget = event.target;

    if (eventTarget instanceof HTMLElement && stickyQuantityInput && eventTarget.contains(stickyQuantityInput)) {
      this.#currentQuantity = event.detail.quantity;
      this.#syncTargetQuantity();
      return;
    }

    this.#currentQuantity = event.detail.quantity;
    this.#syncStickyQuantityInput();
    this.#syncTargetQuantity();
  };

  /**
   * Shows the sticky bar with animation
   */
  #showStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = true;
    stickyBar.dataset.stuck = 'true';
  }

  /**
   * Hides the sticky bar with animation
   */
  #hideStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = false;
    stickyBar.dataset.stuck = 'false';
  }

  // Helper methods
  /**
   * Gets the product form element
   * @returns {HTMLElement | null}
   */
  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const sectionElement = this.closest('.shopify-section');
    if (!sectionElement) return null;

    const sectionId = sectionElement.id.replace('shopify-section-', '');
    return document.querySelector(
      `#shopify-section-${sectionId} product-form-component[data-product-id="${productId}"]`
    );
  }

  /**
   * Gets the initial quantity from the data attribute
   */
  #getInitialQuantity() {
    this.#currentQuantity = parseInt(this.dataset.initialQuantity || '1') || 1;
    this.#syncStickyQuantityInput();
  }

  /**
   * Gets the main product page variant picker
   * @returns {HTMLElement | null}
   */
  #getMainVariantPicker() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const sectionElement = this.closest('.shopify-section');
    if (!sectionElement) return null;

    const sectionId = sectionElement.id.replace('shopify-section-', '');
    return document.querySelector(
      `#shopify-section-${sectionId} variant-picker[data-product-id="${productId}"]`
    );
  }

  /**
   * Gets the sticky quantity selector input
   * @returns {HTMLInputElement | null}
   */
  #getStickyQuantityInput() {
    return this.querySelector('.sticky-add-to-cart__quantity input[name="quantity"]');
  }

  /**
   * Syncs the sticky quantity to the main product form input
   */
  #syncTargetQuantity() {
    const productForm = this.#getProductForm();
    if (!productForm) return;

    const targetQuantityInput = productForm.querySelector('input[name="quantity"]');
    if (!(targetQuantityInput instanceof HTMLInputElement)) return;

    targetQuantityInput.value = this.#currentQuantity.toString();
  }

  /**
   * Syncs the sticky quantity input to the tracked quantity value
   */
  #syncStickyQuantityInput() {
    const stickyQuantityInput = this.#getStickyQuantityInput();
    if (!(stickyQuantityInput instanceof HTMLInputElement)) return;

    stickyQuantityInput.value = this.#currentQuantity.toString();
  }

  /**
   * Updates sticky pill selected states
   * @param {string|number} selectedVariantId
   */
  #syncStickyVariantPills(selectedVariantId) {
    const normalizedId = String(selectedVariantId);
    const pills = this.querySelectorAll('.sticky-add-to-cart__variant-pill');

    pills.forEach((pill) => {
      if (!(pill instanceof HTMLButtonElement)) return;
      const isSelected = pill.dataset.variantId === normalizedId;
      pill.dataset.selected = isSelected ? 'true' : 'false';
      pill.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
    });
  }

  /**
   * Gets product variants JSON from the sticky bar
   * @returns {ProductVariant[]}
   */
  #getVariants() {
    if (this.#variants) return this.#variants;

    const variantsScript = this.querySelector('[data-sticky-add-to-cart-variants]');
    if (!variantsScript?.textContent) {
      this.#variants = [];
      return this.#variants;
    }

    try {
      this.#variants = JSON.parse(variantsScript.textContent);
    } catch {
      this.#variants = [];
    }

    return this.#variants;
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
}
